<template>
  <v-container fluid>
    <v-alert
      v-model="mergeSuccessDialog"
      dismissible
      prominent
      type="success"
      transition="scale-transition"
    >
      Merge successful!
    </v-alert>
    <confirm-delete ref="confirmDelete" />
    <v-dialog
      v-model="newClientDialog"
      max-width="500px"
    >
      <new-client-form
        :key="rerenderNewClientForm"
        @clientCreated="addClient"
      />
    </v-dialog>
    <v-dialog
      v-model="mergeClientDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title class="text-center">
          Merge Duplicate Clients
        </v-card-title>
        <v-card-subtitle>
          Merging duplicate clients will assign
          all inventory and client contacts from the source
          to the destination client, then permanently delete the
          source client.
        </v-card-subtitle>
        <v-card-text>
          <client-auto-complete
            :client="mergeSrcClient"
            :locked="false"
            label-text="Source Client"
            @onClickClient="setMergeSrcClient"
          />
          <client-auto-complete
            :client="mergeDestClient"
            :locked="false"
            label-text="Destination Client"
            @onClickClient="setMergeDestClient"
          />
          <v-btn
            color="error"
            @click="mergeClients()"
          >
            Merge and Delete
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="mergeClientDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="deleteDialog"
      max-width="500px"
    >
      <v-card>
        <ErrorBar />
        <v-card-title>
          <span class="headline">Confirm</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout>
              <span>Are you sure you want to delete {{ confirmDelete.name }}?</span>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            ripple
            @click="deleteDialog = !deleteDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            ripple
            @click="deleteItem(confirmDelete)"
          >
            Yes, Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <h3 class="text-center">
      Clients
    </h3>
    <v-data-table
      class="elevation-1"
      :headers="headers"
      :items="clients"
      :loading="searchPending"
      :options.sync="options"
      :server-items-length="totalClients"
      :footer-props="{'items-per-page-options': [25, 50, 100]}"
      loading-text="loading, please wait"
      @click:row="editItem"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-row>
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Search clients"
              clearable
              class="mr-4 text-left"
            />
            <v-menu
              top
              origin="center center"
              transition="scale-transition"
            >
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  text
                  color="primary"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Actions
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="newClientDialog = true"
                >
                  New Client
                </v-list-item>
                <v-divider />
                <v-list-item
                  @click="mergeClientDialog = true"
                >
                  Merge Duplicate Clients
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>
        </v-toolbar>
      </template>
      <template v-slot:progress>
        <v-progress-linear
          v-show="searchPending"
          indeterminate
          rounded
          height="10"
        />
      </template>
      <template v-slot:item.active="{ item }">
        <v-simple-checkbox
          :value="item.active"
          disabled
        />
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import NewClientForm from './NewClientForm.vue';
import ErrorBar from './ErrorBar.vue';
import ConfirmDelete from './ConfirmDeleteComponent.vue';
import ClientAutoComplete from './ClientAutoComplete.vue';

export default {
  name: 'Clients',
  components: {
    NewClientForm,
    ErrorBar,
    ConfirmDelete,
    ClientAutoComplete,
  },
  data: () => ({
    isFullPage: true,
    totalClients: 0,
    editedIndex: -1,
    editedItem: {},
    editDialog: false,
    newClientDialog: false,
    deleteDialog: false,
    confirmDelete: {},
    searchPending: false,
    mergeClientDialog: false,
    mergeSuccessDialog: false,
    mergeSrcClient: null,
    mergeDestClient: null,
    rerenderNewClientForm: 0,
    search: '',
    searchTimerId: null,
    searchRequestId: 0,
    tableReady: false,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['name'],
      sortDesc: [false],
    },
    headers: [
      {
        text: 'Name',
        value: 'name',
        sortable: true,
      },
      {
        text: 'Phone',
        value: 'phone',
        sortable: false,
      },
      {
        text: 'email',
        value: 'email',
        sortable: false,
      },
      {
        text: 'Address 1',
        value: 'address1',
        sortable: false,
      },
      {
        text: 'Address 2',
        value: 'address2',
        sortable: false,
      },
      {
        text: 'City',
        value: 'city',
        sortable: false,
      },
      {
        text: 'State',
        value: 'state',
        sortable: false,
      },
      {
        text: 'Zip Code',
        value: 'zipCode',
        sortable: false,
      },
      {
        text: 'ICN Abbreviation',
        value: 'icnAbbreviation',
        sortable: true,
      },
      {
        text: 'Item Count',
        value: 'itemCount',
        sortable: true,
      },
      {
        text: 'Active',
        value: 'active',
        sortable: false,
      },
      {
        text: '',
        value: 'actions',
        sortable: false,
      },
    ],
    clients: [],
  }),
  computed: {
    formTitle() {
      return `${this.editedItem.name}`;
    },
    ...mapGetters({
      isLoading: 'apiClient/showLoading',
      hasError: 'apiClient/showError',
    }),
  },
  watch: {
    editDialog(val) {
      this.editDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
    deleteDialog(val) {
      this.deleteDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
    search() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.applySearch();
      }, 400);
    },
    options: {
      handler() {
        if (this.tableReady) {
          this.getClients();
        }
      },
    },
    deep: true,
  },
  mounted() {
    this.tableReady = true;
    this.getClients();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimerId);
  },
  methods: {
    ...mapActions('apiClient', ['get', 'create', 'update', 'del']),
    ...mapMutations('apiClient', ['dismissError']),
    editItem(item) {
      console.log(item);
      const route = this.$router.resolve({ name: 'clientEdit', params: { clientId: item.clientId } });
      window.open(route.href, '_blank');
    },
    async getClients() {
      this.searchRequestId += 1;
      const requestId = this.searchRequestId;
      this.searchPending = true;

      const page = Math.max(this.options.page - 1, 0);
      const size = this.options.itemsPerPage;
      const sortBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : 'name';
      const sortDesc = this.options.sortDesc.length > 0 ? this.options.sortDesc[0] : false;
      const searchText = this.search ? this.search.trim() : '';

      await this.get({
        url: '/clients',
        params: {
          page,
          size,
          sortBy,
          sortDesc,
          searchText: searchText || null,
        },
      }).then(
        (response) => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          this.clients = response.data.data;
          this.totalClients = response.data.totalElements;
        },
        (error) => {
          console.log(error);
        },
      );

      if (requestId === this.searchRequestId) {
        this.searchPending = false;
      }
    },
    applySearch() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = null;

      if (this.options.page === 1) {
        this.getClients();
      } else {
        this.options.page = 1;
      }
    },
    addClient() {
      setTimeout(() => {
        this.newClientDialog = false;
        if (this.options.page === 1) {
          this.getClients();
        } else {
          this.options.page = 1;
        }
      }, 3000);
    },
    setMergeDestClient(client) {
      this.mergeDestClient = client;
    },
    setMergeSrcClient(client) {
      this.mergeSrcClient = client;
    },
    showContacts(client) {
      const route = this.$router.resolve({ name: 'clientContacts', params: { clientId: client.clientId } });
      window.open(route.href, '_blank');
    },
    goToInventory(client) {
      const route = this.$router.resolve({ name: 'inventoryList', params: { clientId: client.clientId, subClientId: client.clientId } });
      window.open(route.href, '_blank');
    },
    async save(item) {
      await this.update({ url: '/clients', body: item, id: item.clientId }).then(
        (response) => {
          this.clients[this.editedIndex] = response.data;
          this.editedItem = item;
          this.editDialog = false;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    showConfirmDelete(item) {
      this.$refs.confirmDelete.open(item.name).then((selection) => {
        if (selection) {
          this.deleteItem(item);
        }
      });
    },
    async deleteItem(item) {
      await this.del({ url: '/clients', id: item.clientId }).then(
        (response) => {
          this.clients.pop(response.data);
          this.deleteDialog = false;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async mergeClients() {
      if (this.mergeSrcClient.clientId && this.mergeDestClient.clientId) {
        await this.create({ url: `/clients/merge/${this.mergeSrcClient.clientId}/${this.mergeDestClient.clientId}` }).then(
          () => {
            this.mergeClientDialog = false;
            this.mergeSuccessDialog = true;
            this.getClients();
          },
          (error) => {
            console.log(error);
          },
        );
      }
    },
  },
};
</script>
